/**
 * Search Index Module
 *
 * Provides fuzzy search across structures and KEBs using Fuse.js.
 */

import Fuse from 'fuse.js';

/**
 * SearchIndex provides fuzzy search capabilities for structures and KEBs.
 */
export class SearchIndex {
  constructor() {
    this.fuse = null;
    this.entries = [];
    this.ready = false;
  }

  /**
   * Initialize search index with structures and KEBs
   * @param {Array} structures - Array of structure objects
   * @param {Array} kebs - Array of KEB objects
   */
  init(structures, kebs) {
    this.entries = [];

    // Add structures
    if (structures && Array.isArray(structures)) {
      structures.forEach((s) => {
        this.entries.push({
          id: s.id,
          name: s.name,
          type: 'structure',
          category: s.category || null,
          description: s.shortDescription || '',
          wikipediaName: s.wikipediaName || '',
          position: s.position,
          resolution: s.resolution
        });
      });
    }

    // Add KEBs
    if (kebs && Array.isArray(kebs)) {
      kebs.forEach((k) => {
        this.entries.push({
          id: k.id,
          name: k.name,
          type: 'keb',
          category: k.type || null,
          description: this.stripHtml(k.description || ''),
          position: k.position,
          resolution: k.resolution,
          linkToMore: k.linkToMore || null
        });
      });
    }

    // Configure Fuse.js
    this.fuse = new Fuse(this.entries, {
      keys: [
        { name: 'name', weight: 0.5 },
        { name: 'category', weight: 0.2 },
        { name: 'description', weight: 0.2 },
        { name: 'wikipediaName', weight: 0.1 }
      ],
      threshold: 0.4, // 0 = exact, 1 = anything matches
      distance: 100, // How far to search in string
      minMatchCharLength: 2,
      includeScore: true,
      shouldSort: true
    });

    this.ready = true;
    console.info(`Search index initialized with ${this.entries.length} entries`);
  }

  /**
   * Search for entries
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @param {number} [options.limit=10] - Max results
   * @param {string} [options.type] - Filter by 'structure' or 'keb'
   * @param {string} [options.category] - Filter by category
   * @returns {Array} Search results
   */
  search(query, options = {}) {
    if (!this.ready || !query || query.length < 2) {
      return [];
    }

    const { limit = 10, type, category } = options;

    // Get more results than needed to allow for filtering
    let results = this.fuse.search(query, { limit: limit * 2 });

    // Apply type filter
    if (type) {
      results = results.filter((r) => r.item.type === type);
    }

    // Apply category filter
    if (category) {
      results = results.filter(
        (r) => r.item.category?.toLowerCase() === category.toLowerCase()
      );
    }

    // Return clean results with score
    return results.slice(0, limit).map((r) => ({
      ...r.item,
      score: r.score
    }));
  }

  /**
   * Get all entries of a specific type
   * @param {string} type - 'structure' or 'keb'
   * @returns {Array}
   */
  getByType(type) {
    return this.entries.filter((e) => e.type === type);
  }

  /**
   * Get all unique categories
   * @returns {Array}
   */
  getCategories() {
    const categories = new Set();
    this.entries.forEach((e) => {
      if (e.category) {
        categories.add(e.category);
      }
    });
    return Array.from(categories).sort();
  }

  /**
   * Get entry by ID and type
   * @param {number} id - Entry ID
   * @param {string} type - 'structure' or 'keb'
   * @returns {Object|null}
   */
  getById(id, type) {
    return this.entries.find((e) => e.id === id && e.type === type) || null;
  }

  /**
   * Strip HTML tags from string
   * @param {string} html - HTML string
   * @returns {string}
   */
  stripHtml(html) {
    // Use a regex approach that works in both browser and Node.js
    if (typeof document !== 'undefined') {
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    }
    // Fallback for Node.js environment (tests)
    return html.replace(/<[^>]*>/g, '');
  }

  /**
   * Check if index is ready for searching
   * @returns {boolean}
   */
  isReady() {
    return this.ready;
  }

  /**
   * Get total entry count
   * @returns {number}
   */
  getCount() {
    return this.entries.length;
  }

  /**
   * Get structure count
   * @returns {number}
   */
  getStructureCount() {
    return this.entries.filter((e) => e.type === 'structure').length;
  }

  /**
   * Get KEB count
   * @returns {number}
   */
  getKebCount() {
    return this.entries.filter((e) => e.type === 'keb').length;
  }
}

// Singleton instance
let instance = null;

/**
 * Get the search index singleton instance
 * @returns {SearchIndex}
 */
export function getSearchIndex() {
  if (!instance) {
    instance = new SearchIndex();
  }
  return instance;
}

/**
 * Reset the singleton (for testing)
 */
export function resetSearchIndex() {
  instance = null;
}
